import {
  type FilterState,
  VALID_ADDITIONAL_OPT_VALUES,
  VALID_AMENITY_VALUES,
  VALID_CREATED_WITHIN_VALUES,
  VALID_LAUNDRY_VALUES,
  VALID_ORDER_BY_VALUES,
  VALID_PET_VALUES,
  VALID_PROPERTY_TYPE_VALUES,
  VALID_RENTAL_TYPE_VALUES,
  VALID_RESTRICTION_VALUES,
} from '@/src/lib/filters/filterTypes';
import {
  normalizeMoveInDate,
  validateDateString,
  validateEnumList,
} from '@/src/lib/filters/filterValidation';
import type { WebMcpInputSchema } from './modelContext';

/**
 * The WebMCP filter vocabulary: one table that carries both each field's JSON Schema and the
 * function that folds it into `FilterState`, so what an agent is told it can send and what the
 * tool actually accepts are generated from the same entry and cannot disagree.
 *
 * Enum values come from the `VALID_*_VALUES` sets in filterTypes.ts, the same source `llms.txt`
 * renders its filter docs from (src/app/llms.txt/llmsTxt.ts) — so the *values* cannot drift. The
 * field list here is maintained separately from that prose and deliberately differs twice:
 * `orderBy` is exposed here but omitted from llms.txt (robots.txt disallows sort URLs, which
 * constrains crawling, not tool calls), and move-in is a `{start}to{end}` range in llms.txt but
 * end-only here, since "available by" is the only half an agent has a use for.
 *
 * The shape is deliberately flat (`priceMin`, not `price: { min }`) — it mirrors the URL params
 * documented in llms.txt, and models fill flat scalars far more reliably than nested objects.
 *
 * `commute` is intentionally absent: it needs a destination latitude and longitude, which an
 * agent has no way to produce from a conversation. Commute stays a UI-only filter.
 */
type FilterField = {
  key: string;
  schema: Record<string, unknown>;
  /** Mutates `next` and returns true, or returns false to leave it untouched and reject the value. */
  apply: (next: FilterState, raw: unknown) => boolean;
};

function numberField(
  key: string,
  description: string,
  assign: (next: FilterState, value: number | null) => void,
): FilterField {
  return {
    key,
    schema: { type: ['integer', 'null'], minimum: 0, description: `${description} Pass null to clear it.` },
    apply: (next, raw) => {
      if (raw === null) {
        assign(next, null);
        return true;
      }
      if (typeof raw !== 'number' || !Number.isFinite(raw) || raw < 0) return false;
      assign(next, raw);
      return true;
    },
  };
}

function listField(
  key: string,
  description: string,
  valid: Set<string>,
  assign: (next: FilterState, value: Array<string>) => void,
): FilterField {
  return {
    key,
    schema: { type: 'array', items: { type: 'string', enum: Array.from(valid) }, description },
    apply: (next, raw) => {
      if (!Array.isArray(raw) || raw.some((entry) => typeof entry !== 'string')) return false;
      const values = raw as Array<string>;
      // An entirely unrecognized list is a mistake worth reporting; clearing is done with [].
      if (values.length > 0 && !values.some((entry) => valid.has(entry))) return false;
      assign(next, validateEnumList(values, valid));
      return true;
    },
  };
}

function enumField(
  key: string,
  description: string,
  valid: Set<string>,
  assign: (next: FilterState, value: string | null) => void,
): FilterField {
  return {
    key,
    schema: { type: ['string', 'null'], enum: [...Array.from(valid), null], description },
    apply: (next, raw) => {
      if (raw === null) {
        assign(next, null);
        return true;
      }
      if (typeof raw !== 'string' || !valid.has(raw)) return false;
      assign(next, raw);
      return true;
    },
  };
}

const FILTER_FIELDS: Array<FilterField> = [
  numberField('priceMin', 'Minimum monthly rent in USD.', (next, value) => (next.price.min = value)),
  numberField('priceMax', 'Maximum monthly rent in USD.', (next, value) => (next.price.max = value)),
  numberField('bedsMin', 'Minimum bedrooms. 0 means studio.', (next, value) => (next.bedrooms.min = value)),
  numberField('bedsMax', 'Maximum bedrooms. Omit for no upper bound.', (next, value) => (next.bedrooms.max = value)),
  numberField('bathsMin', 'Minimum bathrooms.', (next, value) => (next.bathrooms.min = value)),
  numberField('sqftMin', 'Minimum square footage.', (next, value) => (next.sqft.min = value)),
  numberField('sqftMax', 'Maximum square footage.', (next, value) => (next.sqft.max = value)),
  {
    key: 'furnished',
    schema: { type: 'boolean', description: 'Restrict to furnished rentals.' },
    apply: (next, raw) => {
      if (typeof raw !== 'boolean') return false;
      next.furnished = raw;
      return true;
    },
  },
  {
    key: 'keywords',
    schema: {
      type: 'string',
      description: 'Free-text keyword match against the listing. Pass an empty string to clear it.',
    },
    apply: (next, raw) => {
      if (typeof raw !== 'string') return false;
      next.keywords = raw;
      return true;
    },
  },
  listField(
    'pets',
    'Pets that must be allowed.',
    VALID_PET_VALUES,
    (next, value) => (next.pets = value as FilterState['pets']),
  ),
  listField(
    'laundry',
    'Required laundry type.',
    VALID_LAUNDRY_VALUES,
    (next, value) => (next.laundry = value as FilterState['laundry']),
  ),
  listField(
    'amenities',
    'Required amenities.',
    VALID_AMENITY_VALUES,
    (next, value) => (next.amenities = value as FilterState['amenities']),
  ),
  listField(
    'propertyTypes',
    'Building types to include. Listing all of them means the same as no filter.',
    VALID_PROPERTY_TYPE_VALUES,
    (next, value) => (next.propertyTypes = value as FilterState['propertyTypes']),
  ),
  listField(
    'rentalTypes',
    'Rental arrangements to include. Listing all of them means the same as no filter.',
    VALID_RENTAL_TYPE_VALUES,
    (next, value) => (next.rentalTypes = value as FilterState['rentalTypes']),
  ),
  listField(
    'additionalOpts',
    'Listing-quality requirements, such as requiring photos or a published price.',
    VALID_ADDITIONAL_OPT_VALUES,
    (next, value) => (next.additionalOpts = value as FilterState['additionalOpts']),
  ),
  enumField(
    'createdWithin',
    'Only listings published within this window.',
    VALID_CREATED_WITHIN_VALUES,
    (next, value) => (next.createdWithin = value as FilterState['createdWithin']),
  ),
  enumField(
    'restrictions',
    'Occupancy restriction the renter qualifies for. "none" means unrestricted listings only.',
    VALID_RESTRICTION_VALUES,
    (next, value) => (next.restrictions = value as FilterState['restrictions']),
  ),
  enumField(
    'orderBy',
    'Result ordering. Omit for the default relevance order.',
    VALID_ORDER_BY_VALUES,
    (next, value) => (next.orderBy = value),
  ),
  {
    key: 'moveInDateEnd',
    schema: { type: ['string', 'null'], description: 'Latest acceptable move-in date, as YYYY-MM-DD.' },
    apply: (next, raw) => {
      if (raw === null || raw === '') {
        next.moveInDate = { start: null, end: null };
        return true;
      }
      if (typeof raw !== 'string' || !validateDateString(raw)) return false;
      next.moveInDate = normalizeMoveInDate(null, raw);
      return true;
    },
  },
];

export const FILTER_PATCH_SCHEMA: WebMcpInputSchema = {
  type: 'object',
  properties: Object.fromEntries(FILTER_FIELDS.map((field) => [field.key, field.schema])),
  required: [],
};

export type FilterPatchOutcome = {
  filters: FilterState;
  applied: Array<string>;
  rejected: Array<string>;
};

// When only one side of a range is patched and the result crosses the retained bound, clear the
// stale retained bound rather than swapping — the supplied value is the user's intent.
// When both sides are explicitly patched and they cross, swap so the search covers a valid band.
function resolveRangeAfterPatch(
  min: number | null,
  max: number | null,
  minWasPatched: boolean,
  maxWasPatched: boolean,
): { min: number | null; max: number | null } {
  if (min === null || max === null || min <= max) return { min, max };
  if (minWasPatched && !maxWasPatched) return { min, max: null };
  if (maxWasPatched && !minWasPatched) return { min: null, max };
  return { min: max, max: min };
}

/**
 * Folds an agent-supplied patch into the current filter state.
 *
 * Unknown keys and values that fail validation are collected in `rejected` rather than thrown —
 * the tool reports them back so the agent can correct itself, and a single bad field never
 * discards the good ones alongside it.
 */
export function applyFilterPatch(current: FilterState, patch: Record<string, unknown>): FilterPatchOutcome {
  const next: FilterState = {
    ...current,
    price: { ...current.price },
    bedrooms: { ...current.bedrooms },
    bathrooms: { ...current.bathrooms },
    sqft: { ...current.sqft },
    moveInDate: { ...current.moveInDate },
  };
  const applied: Array<string> = [];
  const rejected: Array<string> = [];

  for (const field of FILTER_FIELDS) {
    if (!(field.key in patch)) continue;
    if (field.apply(next, patch[field.key])) applied.push(field.key);
    else rejected.push(field.key);
  }

  const known = new Set(FILTER_FIELDS.map((field) => field.key));
  for (const key of Object.keys(patch)) {
    if (!known.has(key)) rejected.push(key);
  }

  next.price = { ...next.price, ...resolveRangeAfterPatch(next.price.min, next.price.max, 'priceMin' in patch, 'priceMax' in patch) };
  next.sqft = { ...next.sqft, ...resolveRangeAfterPatch(next.sqft.min, next.sqft.max, 'sqftMin' in patch, 'sqftMax' in patch) };
  next.bedrooms = { ...next.bedrooms, ...resolveRangeAfterPatch(next.bedrooms.min, next.bedrooms.max, 'bedsMin' in patch, 'bedsMax' in patch) };

  return { filters: next, applied, rejected };
}

/** Compact, agent-readable description of the filters that are actually set. */
export function describeFilters(filters: FilterState): Record<string, unknown> {
  const described: Record<string, unknown> = {};
  const set = (key: string, value: unknown) => {
    if (value !== null && value !== undefined) described[key] = value;
  };
  const setIfAny = (key: string, values: ReadonlyArray<string>) => {
    if (values.length) described[key] = values;
  };

  set('priceMin', filters.price.min);
  set('priceMax', filters.price.max);
  set('bedsMin', filters.bedrooms.min);
  set('bedsMax', filters.bedrooms.max);
  set('bathsMin', filters.bathrooms.min);
  set('sqftMin', filters.sqft.min);
  set('sqftMax', filters.sqft.max);
  setIfAny('pets', filters.pets);
  setIfAny('laundry', filters.laundry);
  setIfAny('amenities', filters.amenities);
  setIfAny('propertyTypes', filters.propertyTypes);
  setIfAny('rentalTypes', filters.rentalTypes);
  setIfAny('additionalOpts', filters.additionalOpts);
  if (filters.furnished) described.furnished = true;
  if (filters.keywords) described.keywords = filters.keywords;
  set('createdWithin', filters.createdWithin);
  set('restrictions', filters.restrictions);
  set('orderBy', filters.orderBy);
  set('moveInDateEnd', filters.moveInDate.end);
  if (filters.commute) described.commute = { name: filters.commute.name, minutes: filters.commute.minutes };

  return described;
}
